import { Section, Text } from '@/components/atoms';
import UnderlyingYieldBearingAssetsDetailCarouselSection from '@/components/organisms/UnderlyingYieldBearingAssetsDetailCarouselSection/UnderlyingYieldBearingAssetsDetailCarouselSection';
import YieldBearingAssetUnderlyingYieldBearingAssetAllocationChart from '@/components/organisms/YieldBearingAssetPerformanceDetail/YieldBearingAssetUnderlyingYieldBearingAssetAllocationChart';
import YieldBearingAssetUnderlyingYieldBearingAssetAllocationList from '@/components/organisms/YieldBearingAssetPerformanceDetail/YieldBearingAssetUnderlyingYieldBearingAssetAllocationList';
import APP_CONFIG from '@/config/app';
import {
  UnderlyingYieldBearingAssetInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import { calculateEffectiveApy } from '@/utils/functionalUtils';
import { YIELD_BEARING_ASSET_ACTIONS_CARD } from '@/utils/strings';
import { FC, ReactNode, useMemo } from 'react';
import ReturnSection from '../ReturnSection/ReturnSection';
const { MAIN_GAP } = APP_CONFIG.STYLES;
const spacingConfig = {
  gap: `gap${MAIN_GAP}`,
};
interface YieldBearingAssetCompositionSectionProps
  extends Omit<React.ComponentProps<typeof Section>, 'body'> {
  yieldBearingAsset?: YieldBearingAssetInterface;
  underlyingYieldBearingAssets?: UnderlyingYieldBearingAssetInterface[];
  showCompositionDescription?: boolean;
  showCompositionChart?: boolean;
  isCompositionDifferentFromOriginal?: boolean;
  showDetails?: boolean;
  iconIntent?:
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'error'
    | 'disabled'
    | 'warning';
  canChangeComposition?: boolean;
  onCompositionChange?: (
    newAllocations: YieldBearingAssetInterface['underlyingYieldBearingAssets'],
  ) => void;
  onCompositionChangeDone?: (
    newAllocations: YieldBearingAssetInterface['underlyingYieldBearingAssets'],
  ) => void;
  headerTitle?: string | ReactNode;
  subHeader?: string | ReactNode;
  showHeader?: boolean;
  showDescription?: boolean;
  showAssetDescription?: boolean;
  showSource?: boolean;
  isFullBleed?: boolean;
  isBackgroundTransparent?: boolean;
  showAssetDetailsAsChips?: boolean;
  showHistoricalPerformanceCharts?: boolean;
  showDefaultAndPersonalizedReturn?: boolean;
  onBrochureHelpClick?: (
    markdown: string,
    asset: UnderlyingYieldBearingAssetInterface,
  ) => void;
}
const YieldBearingAssetCompositionSection: FC<
  YieldBearingAssetCompositionSectionProps
> = ({
  yieldBearingAsset,
  underlyingYieldBearingAssets,
  showCompositionDescription = false,
  showCompositionChart = true,
  isCompositionDifferentFromOriginal = false,
  showDetails = true,
  iconIntent = 'primary',
  canChangeComposition = false,
  onCompositionChange,
  onCompositionChangeDone,
  headerTitle,
  subHeader,
  showHeader = true,
  showDescription = true,
  showAssetDescription = true,
  showSource = true,
  isFullBleed = false,
  isBackgroundTransparent = false,
  showAssetDetailsAsChips = false,
  showHistoricalPerformanceCharts = false,
  showDefaultAndPersonalizedReturn = false,
  onBrochureHelpClick,
  ...props
}) => {
  const resolvedUnderlyingYieldBearingAssets =
    underlyingYieldBearingAssets ||
    yieldBearingAsset?.underlyingYieldBearingAssets ||
    [];
  const compositionDescription = showCompositionDescription
    ? (() => {
        const isSingleAsset = resolvedUnderlyingYieldBearingAssets.length === 1;
        const ticker = yieldBearingAsset?.ticker || 'Asset';
        const compositionDescriptionPrefix = isCompositionDifferentFromOriginal
          ? `You chose to personalize ${ticker}'s composition.`
          : '';
        const compositionSentenceStart = compositionDescriptionPrefix
          ? ` It is composed of`
          : `${ticker} is composed of`;
        if (isSingleAsset) {
          return `${compositionDescriptionPrefix}${compositionSentenceStart} 100% ${resolvedUnderlyingYieldBearingAssets[0].name}.`;
        } else if (resolvedUnderlyingYieldBearingAssets.length === 2) {
          return `${compositionDescriptionPrefix}${compositionSentenceStart} ${(
            resolvedUnderlyingYieldBearingAssets[0].composition * 100
          ).toFixed(2)}% ${resolvedUnderlyingYieldBearingAssets[0].name} and ${(
            resolvedUnderlyingYieldBearingAssets[1].composition * 100
          ).toFixed(2)}% ${resolvedUnderlyingYieldBearingAssets[1].name}.`;
        } else {
          return `${compositionDescriptionPrefix}${compositionSentenceStart} ${resolvedUnderlyingYieldBearingAssets
            .map((underlyingAsset, index, array) => {
              const percentage = (underlyingAsset.composition * 100).toFixed(2);
              if (index === array.length - 1) {
                return `and ${percentage}% ${underlyingAsset.name}`;
              }
              return `${percentage}% ${underlyingAsset.name}`;
            })
            .join(', ')}.`;
        }
      })()
    : '';
  const personalizedApy = useMemo(
    () =>
      calculateEffectiveApy(
        resolvedUnderlyingYieldBearingAssets.map((asset) => ({
          apy: asset.apy,
          tvl: asset.tvlUSD,
          composition: asset.composition,
        })),
      ),
    [resolvedUnderlyingYieldBearingAssets],
  );
  const body: ReactNode = (
    <div className={`flex flex-col w-full ${spacingConfig.gap}`}>
      {showCompositionDescription && <Text value={compositionDescription} />}
      {showDetails && (
        <>
          {(canChangeComposition || showDefaultAndPersonalizedReturn) && (
            <div className={`flex flex-row ${spacingConfig.gap}`}>
              <ReturnSection
                label={
                  YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.COMPOSITION
                    .DEFAULT_RETURN_LABEL
                }
                asset={yieldBearingAsset}
              />
              <ReturnSection
                label={
                  YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.COMPOSITION
                    .PERSONALIZED_RETURN_LABEL
                }
                returnRate={personalizedApy}
              />
            </div>
          )}
          {showCompositionChart && (
            <YieldBearingAssetUnderlyingYieldBearingAssetAllocationChart
              underlyingYieldBearingAssets={
                resolvedUnderlyingYieldBearingAssets
              }
            />
          )}
          {canChangeComposition ? (
            <YieldBearingAssetUnderlyingYieldBearingAssetAllocationList
              underlyingYieldBearingAssets={
                resolvedUnderlyingYieldBearingAssets
              }
              canChangeComposition={canChangeComposition}
              onCompositionChange={onCompositionChange}
              onCompositionChangeDone={onCompositionChangeDone}
            />
          ) : showDescription ? (
            <UnderlyingYieldBearingAssetsDetailCarouselSection
              underlyingYieldBearingAssets={
                resolvedUnderlyingYieldBearingAssets
              }
              showCompositionColor
              showDescription={showAssetDescription}
              showSource={showSource}
              showAssetDetailsAsChips={showAssetDetailsAsChips}
              showHistoricalPerformanceCharts={showHistoricalPerformanceCharts}
              onBrochureHelpClick={onBrochureHelpClick}
            />
          ) : (
            <YieldBearingAssetUnderlyingYieldBearingAssetAllocationList
              underlyingYieldBearingAssets={
                resolvedUnderlyingYieldBearingAssets
              }
            />
          )}
        </>
      )}
    </div>
  );
  return (
    <Section
      headerTitle={
        headerTitle ||
        (showHeader
          ? canChangeComposition
            ? YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.COMPOSITION.HEADER
                .WHEN_COMPOSITION_IS_EDITABLE
            : YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.COMPOSITION.HEADER
                .WHEN_COMPOSITION_IS_NOT_EDITABLE
          : '')
      }
      subHeader={subHeader}
      isFullBleed={isFullBleed}
      isBackgroundTransparent={isBackgroundTransparent}
      body={body}
      {...props}
    />
  );
};
export default YieldBearingAssetCompositionSection;
